import {
	type Asset,
	type AssetType,
	type AssetTypeStatus,
	type Document,
	type Form,
	type FormSubmission,
	includesLowercase,
	type Issue,
	type IssueType,
	type IssueTypePriority,
	type IssueTypeStatus,
	type ProcedureInitiative,
	type ProcedureType,
	type Project,
	type Stored,
	type Team,
	type User,
	UNLABELLED_ASSET_LABEL,
	UNTITLED_DOCUMENT_TITLE,
	UNTITLED_ISSUE_TITLE,
	INDEX_FALLBACK,
} from "@overmap-ai/common"

import { ProcedureStepCompletionStatus } from "../enums/procedures"
import { procedureStepCompletionStatusData } from "../mappings/procedures"

export const assetFilterCallback = (asset: Stored<Asset>, filterValue: string) => {
	return (
		includesLowercase(asset.label || UNLABELLED_ASSET_LABEL, filterValue) ||
		includesLowercase(asset.index?.toString() ?? INDEX_FALLBACK, filterValue)
	)
}

export const issueFilterCallback = (issue: Stored<Issue>, filterValue: string) => {
	return (
		includesLowercase(issue.title || UNTITLED_ISSUE_TITLE, filterValue) ||
		includesLowercase(issue.index?.toString() ?? INDEX_FALLBACK, filterValue)
	)
}

export const formSubmissionFilterCallback = (formSubmission: Stored<FormSubmission>, filterValue: string) => {
	return includesLowercase(formSubmission.index?.toString() ?? INDEX_FALLBACK, filterValue)
}

export const assetTypeFilterCallback = (assetType: Stored<AssetType>, filterValue: string) => {
	return includesLowercase(assetType.name, filterValue) || includesLowercase(assetType.abbr, filterValue)
}

export const issueTypeFilterCallback = (issueType: Stored<IssueType>, filterValue: string) => {
	return includesLowercase(issueType.name, filterValue) || includesLowercase(issueType.abbr, filterValue)
}

export const procedureTypeFilterCallback = (procedureType: Stored<ProcedureType>, filterValue: string) => {
	return includesLowercase(procedureType.name, filterValue) || includesLowercase(procedureType.abbr, filterValue)
}

export const teamFilterCallback = (team: Stored<Team>, filterValue: string) => {
	return includesLowercase(team.name, filterValue)
}

export const projectFilterCallback = (project: Project, filterValue: string) => {
	return includesLowercase(project.name, filterValue)
}

export const userFilterCallback = (user: User, filterValue: string) => {
	return includesLowercase(user.username, filterValue) || includesLowercase(user.email, filterValue)
}

export const assigneeFilterCallback = (user: User | null, filterValue: string) => {
	if (user === null) {
		return includesLowercase("Unassigned", filterValue)
	}
	return userFilterCallback(user, filterValue)
}

export const issueTypeStatusFilterCallback = (issueTypeStatus: Stored<IssueTypeStatus>, filterValue: string) => {
	return includesLowercase(issueTypeStatus.name, filterValue)
}

export const assetTypeStatusFilterCallback = (assetTypeStatus: Stored<AssetTypeStatus>, filterValue: string) => {
	return includesLowercase(assetTypeStatus.name, filterValue)
}

export const issuePriorityFilterCallback = (issueTypePriority: Stored<IssueTypePriority>, filterValue: string) => {
	return includesLowercase(issueTypePriority.name, filterValue)
}

export const formFilterCallback = (form: Stored<Form>, filterValue: string) => {
	return includesLowercase(form.name, filterValue) || includesLowercase(form.abbr, filterValue)
}

export const procedureStepCompletionStatusFilterCallback = (
	value: ProcedureStepCompletionStatus,
	filterValue: string,
) => {
	return includesLowercase(procedureStepCompletionStatusData[value].label, filterValue)
}

export const procedureInitiativeFilterCallback = (
	procedureInitiative: Stored<ProcedureInitiative>,
	filterValue: string,
) => {
	return includesLowercase(procedureInitiative.index?.toString() ?? INDEX_FALLBACK, filterValue)
}

export const documentFilterCallback = (document: Stored<Document>, filterValue: string) => {
	return includesLowercase(document.title || UNTITLED_DOCUMENT_TITLE, filterValue)
}
